import {useState, useEffect} from 'react';
import { 
    Table, 
    TableHeader, 
    TableHeaderCell, 
    TableBody, TableRow, 
    TableCell,
    Button, 
    Tooltip,
    useToastController,
  } from "@fluentui/react-components";
import { ChevronLeftRegular, ChevronRightRegular, Info32Filled, Info32Regular } from "@fluentui/react-icons";
import { format, startOfMonth, addMonths, subMonths } from "date-fns";
import { useGlobalStatus } from '../../services/GlobalStatusProvider';
import '../timesheet/Timesheet.css';
import TooltipWatcherWidget from '../TooltipWatcherWidget';
import { useNavigate } from "react-router-dom";
import { GetA23MonthlySubmissionStatus, UnsubmitMonthlySubmission } from '../../services/apiService'
import { showErrorToast, showSuccessToast, TOASTER_ID } from "../../services/toastUtils";
import { Stack } from '@fluentui/react';


export default function MonthlyTimesheetStatus({ data, setData, currentMonthStart, setCurrentMonthStart }) {
  const { startLoad, endLoad, useStyles } = useGlobalStatus();
  const { dispatchToast } = useToastController(TOASTER_ID);
  const styles = useStyles();
  const navigate = useNavigate();
  const [infoActive, setInfoActive] = useState(false);
  const [timesheetData, setTimesheetData] = useState([]);

  const handleInfoToggle = () => {
    document.body.classList.contains("show-tooltip") ? document.body.classList.remove('show-tooltip') : document.body.classList.add('show-tooltip');
    setInfoActive(!infoActive);
  }

  const loadWeekData = async (weekStart) => {
    console.log(weekStart);
      startLoad();
      // API request to get the data for the week
      let dataForWeek = [];
      try {
        dataForWeek = await GetA23MonthlySubmissionStatus(weekStart);
      } catch (error) {
        showErrorToast(dispatchToast, "Request failed or timed out");
      }
      
      setTimesheetData(dataForWeek);
      endLoad();
    };

  const handleDateChange = async (change) => {
    startLoad();

    const newMonthStart = change === 'previous'
    ? startOfMonth(subMonths(currentMonthStart, 1))
    : startOfMonth(addMonths(currentMonthStart, 1));

    setCurrentMonthStart(newMonthStart);
    setData(data);
    loadWeekData(newMonthStart);
    endLoad();
  };

  const handleUnsubmit = async (id, name) => {
    try {
      await UnsubmitMonthlySubmission(id, currentMonthStart);
      showSuccessToast(dispatchToast, `Successfully unsubmitted month for ${name}`);
    } catch (error) {
      showErrorToast(dispatchToast, error);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await loadWeekData(currentMonthStart);
    };
    fetchData();
  }, []);

  return (
    <div className="timesheet-container">
      <div className="navigation-controls">
        <TooltipWatcherWidget text="Click to go to Admin Home" direction='above'>
          <Button className={styles.buttonPrimary} onClick={() => navigate("/")}>Home</Button>
        </TooltipWatcherWidget>
        <div className="nav-center">
          <TooltipWatcherWidget text="Cycle month">
          <Button
            className={styles.buttonSecondary}
            size="large"
            icon={<ChevronLeftRegular aria-hidden="true" />}
            onClick={() => handleDateChange("previous")}
            aria-label="Go to previous month"
          >
            Prev
          </Button>
          </TooltipWatcherWidget>
          <span className="month-label" id="month-label">
            {format(currentMonthStart, "LLLL yyyy")}
          </span>
          <Button
            className={styles.buttonSecondary}
            size="large"
            icon={<ChevronRightRegular aria-hidden="true" />}
            onClick={() => handleDateChange("next")}
            aria-label="Go to next month"
          >
            Next
          </Button>
        </div>
        <Tooltip 
          withArrow
          positioning="before"
          relationship="description"
          content={{children: "Click to toggle help", className: styles.tooltip}}>
        {infoActive ? (
          <Info32Filled className="tooltip-filled" onClick={handleInfoToggle} />
        ) : (
          <Info32Regular className="tooltip" onClick={handleInfoToggle} />
        )}
      </Tooltip>
    </div>
  
      <Table className="approval-table">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>User</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TooltipWatcherWidget text="Used to unlock monthly submissions for users that need to update their timesheet during the month. 
            To be used in conjunction with a rejection on Projectworks for the particular week needed to be changed and a sync down." direction='below-start'>
              <TableHeaderCell>Unsubmit Monthly Submission</TableHeaderCell>
            </TooltipWatcherWidget>
          </TableRow>
        </TableHeader>
        <TableBody>
          {timesheetData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2}>No users have submitted!</TableCell>
            </TableRow>
          ) : (
            timesheetData.map((user, index) => (
              <TableRow key={index}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.timesheetStatus}</TableCell>
                <TableCell>
                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <Button className={styles.buttonPrimary} onClick={() => handleUnsubmit(user.id, user.name)}>Unsubmit</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
  
}
